import { ALL_CONTACT_TYPES } from './contactTypeConfig';
import { hasAboutMeContent } from './profilePayload';

/**
 * Shared field registry + display logic for "what I'm sharing" UIs.
 *
 * Single source of truth used by BOTH the SharingSelectionOverlay (bottom sheet,
 * QR/chat flow) and the inline SHARED list on the contact detail page, so the two
 * never diverge. `aboutMe` is handled separately by callers (it's a toggle, not a
 * contact field).
 */

/**
 * Format a phone number for display.
 * US numbers: (XXX) XXX-XXXX (no +1). International: +digits.
 */
export const formatPhoneNumber = (phone) => {
  if (!phone) return phone;

  const hasPlus = phone.startsWith('+');
  const digits = phone.replace(/\D/g, '');

  if ((digits.startsWith('1') && digits.length === 11) || digits.length === 10) {
    const nationalNumber = digits.length === 11 ? digits.slice(1) : digits;
    const areaCode = nationalNumber.slice(0, 3);
    const exchange = nationalNumber.slice(3, 6);
    const subscriber = nationalNumber.slice(6, 10);
    return `(${areaCode}) ${exchange}-${subscriber}`;
  }

  if (hasPlus || digits.length > 10) {
    return `+${digits}`;
  }

  return phone;
};

/**
 * Does a phone/whatsapp value have enough digits to be valid (>= 7)?
 */
export const hasValidPhoneDigits = (value) => {
  if (!value) return false;
  const digits = value.replace(/\D/g, '');
  return digits.length >= 7;
};

/**
 * Field definitions — primary, secondary, and expanded contact types.
 * `aboutMe` is intentionally excluded (handled separately as a toggle).
 */
// Display order for the sharing UIs. Icon and label come from ALL_CONTACT_TYPES:
// the contact-type registry is the one source, so a type added or restyled there
// lands here without a second declaration. This list used to re-declare its own
// icons, which is how email drifted (Envelope here, EnvelopeSimpleOpen in the
// registry) — deriving closes that by construction.
const SHARING_FIELD_KEYS = [
  'phone',
  'phone2',
  'email',
  'email2',
  'instagram',
  'instagram2',
  'linkedin',
  'whatsapp',
  'whatsapp2',
  'facebook',
  'github',
  'signal',
  'snapchat',
  'substack',
  'telegram',
  'tiktok',
  'twitch',
  'twitter',
  'youtube',
  'custom',
];

export const SHARING_FIELD_DEFINITIONS = SHARING_FIELD_KEYS.map((key) => {
  // 'phone2' shares 'phone''s registry entry — the numeral is a slot, not a type
  const type = key.replace(/\d+$/, '');
  const config = ALL_CONTACT_TYPES[type];
  return { key, icon: config.Icon, label: config.label, type };
});

/**
 * Compute the shareable fields the user actually has, with display labels.
 *
 * If multiple values exist for a type (e.g. phone + phone2), the actual value is
 * shown; otherwise the type label. Custom always shows its value. Excludes aboutMe.
 *
 * Each entry also carries `valueLabel` — the field's actual value, formatted.
 * The bottom sheet labels by type ("Phone"); the detail page's shared ledger
 * labels by value ("917-650-2256") per the 2026-08-18 frames. Same source, two
 * presentations, so the two can't drift apart on which fields exist.
 *
 * @param {Object|null} profileData - The user's own profile data
 * @returns {Array<{key, icon, label, valueLabel, type}>}
 */
export const getAvailableSharingFields = (profileData) => {
  if (!profileData) return [];

  const hasValue = {
    phone: hasValidPhoneDigits(profileData.phone),
    email: !!profileData.email,
    instagram: !!profileData.instagram,
    linkedin: !!profileData.linkedin,
    whatsapp: hasValidPhoneDigits(profileData.whatsapp),
    phone2: hasValidPhoneDigits(profileData.phone2),
    email2: !!profileData.email2,
    instagram2: !!profileData.instagram2,
    whatsapp2: hasValidPhoneDigits(profileData.whatsapp2),
    facebook: !!profileData.facebook,
    github: !!profileData.github,
    signal: !!profileData.signal,
    snapchat: !!profileData.snapchat,
    substack: !!profileData.substack,
    telegram: !!profileData.telegram,
    tiktok: !!profileData.tiktok,
    twitch: !!profileData.twitch,
    twitter: !!profileData.twitter,
    youtube: !!profileData.youtube,
    custom: !!profileData.custom,
  };

  const typeCount = {
    phone: (hasValue.phone ? 1 : 0) + (hasValue.phone2 ? 1 : 0),
    email: (hasValue.email ? 1 : 0) + (hasValue.email2 ? 1 : 0),
    instagram: (hasValue.instagram ? 1 : 0) + (hasValue.instagram2 ? 1 : 0),
    whatsapp: (hasValue.whatsapp ? 1 : 0) + (hasValue.whatsapp2 ? 1 : 0),
    linkedin: hasValue.linkedin ? 1 : 0,
    facebook: hasValue.facebook ? 1 : 0,
    github: hasValue.github ? 1 : 0,
    signal: hasValue.signal ? 1 : 0,
    snapchat: hasValue.snapchat ? 1 : 0,
    substack: hasValue.substack ? 1 : 0,
    telegram: hasValue.telegram ? 1 : 0,
    tiktok: hasValue.tiktok ? 1 : 0,
    twitch: hasValue.twitch ? 1 : 0,
    twitter: hasValue.twitter ? 1 : 0,
    youtube: hasValue.youtube ? 1 : 0,
    custom: hasValue.custom ? 1 : 0,
  };

  return SHARING_FIELD_DEFINITIONS.filter(({ key }) => hasValue[key]).map(
    ({ key, icon, label, type }) => {
      const raw = profileData[key];
      const formatted = type === 'phone' || type === 'whatsapp' ? formatPhoneNumber(raw) : raw;

      let displayLabel = label;
      if ((typeCount[type] > 1 || type === 'custom') && raw) {
        displayLabel = formatted;
      }
      return { key, icon, label: displayLabel, valueLabel: formatted || label, type };
    }
  );
};

/**
 * Read the global last-shared field set (localStorage `profile_last_shared_fields`).
 * Returns the parsed array, or null when absent, malformed, or empty — callers
 * treat "no usable set" as one state. Written by commitSharedFields (unless
 * updateGlobalDefaults:false); consumed by the quick-share row's Last-share gate.
 *
 * @returns {string[]|null}
 */
export const readLastSharedFields = () => {
  try {
    const stored = JSON.parse(localStorage.getItem('profile_last_shared_fields') || 'null');
    return Array.isArray(stored) && stored.length > 0 ? stored : null;
  } catch {
    return null;
  }
};

/**
 * Read the global name-sharing default (localStorage `profile_name_sharing`).
 * Defaults to 'full' when unset — the same default every consumer uses.
 *
 * @returns {'full'|'first'}
 */
export const readGlobalNameSharing = () =>
  localStorage.getItem('profile_name_sharing') === 'first' ? 'first' : 'full';

/**
 * Should the quick-share row show the "Last share" (Repeat) button?
 *
 * True when the stored last-shared set has at least one contact field that still
 * resolves to a value on the profile, AND the user has more than one available
 * contact — with a single contact, "Last share" would just duplicate the Phone
 * button. Non-contact keys in the stored set (photoUrl, aboutMe, currentCity)
 * don't count: they aren't in the sharing field registry.
 *
 * @param {string[]|null} lastSharedFields - Parsed profile_last_shared_fields, or null
 * @param {Object|null} profileData - The user's own profile data
 * @returns {boolean}
 */
/**
 * Would "Full profile" send nothing the single channel pill doesn't?
 *
 * The SAME rule canQuickLastShare already applies to the recall pill ("with a
 * single contact, 'Last share' would just duplicate the Phone button"), applied
 * to the other button with the same problem: a name-and-phone profile was
 * showing [Full profile] AND [Phone] — two buttons, one payload (D27, user
 * ruling 2026-08-17).
 *
 * The delta really is only the channel set. Both share recipes already carry
 * photo and city (handleSheetTypeShare sends ['photoUrl', typeId,
 * ...currentCity]; handleSheetFullProfile sends ['photoUrl', ...channelKeys,
 * ...currentCity]), and the photo ships at formation anyway. So "is Full
 * profile redundant?" reduces to "does it carry more than one channel?"
 *
 * Deliberately conservative — it requires the row-one pill to BE the single
 * channel Full profile would send. availableKeys is broader than the contact
 * pill list (custom links, *2 secondaries, content channels), so a host with a
 * phone AND a custom link has one pill but a two-channel Full profile;
 * collapsing there would silently drop the custom link from the one-tap.
 *
 * @param {string[]} availableKeys - getAvailableSharingFields(profile) keys —
 *   exactly what "Full profile" would send
 * @param {string} primaryTypeId - the contact type that would take row one
 * @returns {boolean}
 */
export const isFullProfileRedundant = (availableKeys, primaryTypeId) =>
  Array.isArray(availableKeys) && availableKeys.length === 1 && primaryTypeId === availableKeys[0];

export const canQuickLastShare = (lastSharedFields, profileData) => {
  if (!Array.isArray(lastSharedFields) || lastSharedFields.length === 0) return false;
  const available = getAvailableSharingFields(profileData);
  if (available.length <= 1) return false;
  const availableKeys = new Set(available.map((f) => f.key));
  return lastSharedFields.some((key) => availableKeys.has(key));
};

/**
 * Label for the share sheet's black last-share pill: full type names of the stored
 * set's contact fields that still resolve on the profile — distinct types, two
 * named max, count marker for the rest ("Phone, Instagram + 1").
 *
 * Returns null when nothing resolves OR the resolving set is phone-type-only —
 * the sheet's dedicated Phone pill already covers that share, so the last-share
 * variant never appears (the epic's dedup rule).
 *
 * @param {string[]|null} lastSharedFields - Parsed profile_last_shared_fields, or null
 * @param {Object|null} profileData - The user's own profile data
 * @returns {string|null}
 */
export const formatLastShareLabel = (lastSharedFields, profileData) => {
  if (!Array.isArray(lastSharedFields) || lastSharedFields.length === 0) return null;
  const availableKeys = new Set(getAvailableSharingFields(profileData).map((f) => f.key));
  // Stored-set order: the label names what was shared, in the order it was shared
  const resolved = lastSharedFields
    .filter((key) => availableKeys.has(key))
    .map((key) => SHARING_FIELD_DEFINITIONS.find((def) => def.key === key))
    .filter(Boolean);
  if (resolved.length === 0) return null;
  if (resolved.every(({ type }) => type === 'phone')) return null;

  const typeLabels = [];
  const seenTypes = new Set();
  for (const { type, label } of resolved) {
    if (seenTypes.has(type)) continue;
    seenTypes.add(type);
    typeLabels.push(label);
  }
  if (typeLabels.length <= 2) return typeLabels.join(', ');
  return `${typeLabels[0]}, ${typeLabels[1]} + ${typeLabels.length - 2}`;
};

/**
 * What `Full profile` sends — FULL: the photo, every channel, the city and the
 * About-me bundle (user ruling, 2026-08-20). One home for the list, shared by
 * the conversation page's sheet and the new code page's Share sheet (signup
 * E3a) — aboutMe was once missing from a hand-built copy, so there is one.
 *
 * @param {Object} profileData - the own profile
 * @returns {string[]} field keys for commitSharedFields
 */
export const fullProfileFields = (profileData) => [
  'photoUrl',
  ...getAvailableSharingFields(profileData).map((f) => f.key),
  ...(profileData?.currentCity ? ['currentCity'] : []),
  ...(hasAboutMeContent(profileData) ? ['aboutMe'] : []),
];
// (The order above is the live conversation page's, byte-for-byte; Custom's
// DISPLAY order — Lives in, About me, then the channels — is fullProfileRows'.)

/**
 * The same list as rows to tick (the photo always goes, so it has none):
 * `Lives in <city>`, `About me`, then every channel — the order every share
 * sheet uses (DETAIL §8e, CD Drop 17) — the new code page's Custom (signup E3a).
 *
 * @param {Object} profileData - the own profile
 * @returns {Array<{key: string, label: string, icon?: Object}>}
 */
export const fullProfileRows = (profileData) => [
  ...(profileData?.currentCity
    ? [{ key: 'currentCity', label: `Lives in ${profileData.currentCity}` }]
    : []),
  ...(hasAboutMeContent(profileData) ? [{ key: 'aboutMe', label: 'About me' }] : []),
  ...getAvailableSharingFields(profileData).map((f) => ({
    key: f.key,
    label: f.valueLabel || f.label,
    icon: f.icon,
  })),
];
